import { useEffect, useMemo, useState } from "react";
import { Hourglass, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Slim countdown chip for the BookingCart drawer. Mirrors the portal banner
 * but with a light-theme palette to match the cart's white background.
 *
 * Fetches `/api/client/credits` (auth-required); if the visitor isn't signed
 * in or has no active holds, the chip renders nothing — so it's safe to drop
 * into anonymous-cart flows.
 */
const CartPendingHoldChip = () => {
  const [credits, setCredits] = useState([]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("client_token");
        if (!token) return;
        const res = await fetch(`${API}/api/client/credits`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setCredits(Array.isArray(data?.credits) ? data.credits : []);
      } catch {
        /* anonymous / offline — silently skip */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const soonest = useMemo(() => {
    const n = Date.now();
    return (credits || [])
      .filter((c) => c && c.pending_booking_until)
      .map((c) => ({ credit: c, expiresMs: Date.parse(c.pending_booking_until) }))
      .filter((h) => Number.isFinite(h.expiresMs) && h.expiresMs > n)
      .sort((a, b) => a.expiresMs - b.expiresMs)[0] || null;
  }, [credits]);

  useEffect(() => {
    if (!soonest) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [soonest]);

  if (!soonest) return null;
  const remaining = Math.max(0, Math.floor((soonest.expiresMs - now) / 1000));
  if (remaining <= 0) return null;

  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;
  const timeStr = `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  const urgent = remaining <= 180;

  const wrapClass = urgent
    ? "bg-rose-50 border-rose-300 text-rose-900"
    : "bg-amber-50 border-amber-300 text-amber-900";
  const pillClass = urgent
    ? "bg-rose-200 text-rose-900"
    : "bg-amber-200 text-amber-900";

  return (
    <div
      className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 ${wrapClass}`}
      data-testid="cart-pending-hold-chip"
    >
      {urgent ? (
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
      ) : (
        <Hourglass className="w-4 h-4 flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-bold uppercase tracking-wider leading-tight">
          Points on hold
        </p>
        <p className="text-[10px] opacity-80 truncate">
          Apply <span className="font-mono font-bold">{soonest.credit.credit_code}</span> at
          checkout · ${soonest.credit.dollar_value} credit
        </p>
      </div>
      <span
        className={`text-sm font-mono font-bold tabular-nums px-2 py-0.5 rounded ${pillClass}`}
        data-testid="cart-pending-hold-timer"
      >
        {timeStr}
      </span>
    </div>
  );
};

export default CartPendingHoldChip;
